<template>
  <div class="horizontal-card">
    <article
      class="horizontal-card__card"
      :aria-labelledby="titleId"
      role="article">
      <!-- Content Section -->
      <div
        class="horizontal-card__content"
        role="main">
        <p class="horizontal-card__title">
          {{ title }}
        </p>

        <p
          v-if="description"
          :id="descriptionId"
          class="horizontal-card__description">
          {{ description }}
        </p>

        <div
          v-if="showCtas"
          class="horizontal-card__ctas"
          role="group"
          :aria-labelledby="titleId"
          :aria-describedby="description ? descriptionId : undefined">
          <cta
            v-if="primaryCtaText"
            :text="primaryCtaText"
            variant="solid"
            color="white"
            :show-icon="true"
            :animated-icon="true"
            class="horizontal-card__cta"
            :aria-label="`${primaryCtaText} - ${title}`"
            :to="localePath('contacts')" />

          <cta
            v-if="secondaryCtaText"
            :text="secondaryCtaText"
            variant="solid"
            color="white"
            :show-icon="true"
            :animated-icon="true"
            class="horizontal-card__cta"
            :aria-label="`${secondaryCtaText} - ${title}`"
            :to="localePath('store-locator')" />
        </div>
      </div>

      <!-- Image Section -->
      <div class="horizontal-card__image">
        <base-image
          v-if="imageSrc"
          :src="imageSrc"
          :avif="imageAvif"
          :webp="imageWebp"
          :width="2000"
          :height="1079"
          :alt="imageAlt || title"
          :lazy="false"
          class="horizontal-card__image-element" />
      </div>
    </article>
  </div>
</template>

<script setup>
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'

  import Cta from './Cta.vue'

  // Component registration
  defineOptions({
    components: {
      Cta
    }
  })

  const { t } = useI18n()
  const localePath = useLocalePath()

  // Props with comprehensive validation
  const props = defineProps({
    title: {
      type: String,
      default: 'Sei interessato?',
      validator: value => typeof value === 'string' && value.trim().length > 0
    },
    description: {
      type: String,
      default: 'Contattaci e scopri il mondo Veneta Cucina.',
      validator: value =>
        value === null
        || value === ''
        || (typeof value === 'string' && value.trim().length > 0)
    },
    imageSrc: {
      type: String,
      default: null,
      validator: value =>
        value === null || (typeof value === 'string' && value.trim().length > 0)
    },
    imageAvif: {
      type: String,
      default: null
    },
    imageWebp: {
      type: String,
      default: null
    },
    imageAlt: {
      type: String,
      default: null
    },
    primaryCtaText: {
      type: String,
      default: 'SCOPRI DI PIÙ',
      validator: value => typeof value === 'string' && value.trim().length > 0
    },
    secondaryCtaText: {
      type: String,
      default: 'SCOPRI DI PIÙ',
      validator: value =>
        value === null
        || value === ''
        || (typeof value === 'string' && value.trim().length > 0)
    },
    showCtas: {
      type: Boolean,
      default: true
    }
  })

  // Emits
  const emit = defineEmits([ 'primary-cta-click', 'secondary-cta-click' ])

  // Computed properties
  const titleId = computed(
    () => `horizontal-card-title-${Math.random().toString(36).substr(2, 9)}`
  )
  const descriptionId = computed(
    () => `horizontal-card-desc-${Math.random().toString(36).substr(2, 9)}`
  )
</script>

<style lang="scss" scoped>
.horizontal-card {
  @include blocks-y-spacing;

  &__card {
    display: flex;
    gap: 32px;
    border-radius: 10px;
    overflow: hidden;
    height: 370px;
    background: var(--color-red);
    box-shadow: 0 4px 20px rgb(0, 0, 0, 0.08);
    transition:
      box-shadow var(--timing-default) var(--easing-default),
      transform var(--timing-default) var(--easing-default);
    position: relative;

    &:hover {
      box-shadow: 0 8px 32px rgb(0, 0, 0, 0.12);
      transform: translateY(-2px);
    }

    &:focus-within {
      outline: 2px solid var(--color-red);
      outline-offset: 2px;
    }

    @include up-to-tablet {
      flex-direction: column;
      gap: 0;
      height: auto;
    }
  }

  &__content {
    flex: 1;
    background: var(--color-red);
    color: var(--color-white);
    padding: 40px 0 40px 40px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0;
    position: relative;
    z-index: 1;
    min-width: 0;

    /* Prevents flex item from overflowing */
  }

  &__title {
    font-family: var(--font-family-poppins);
    font-size: 40px;
    font-weight: var(--font-weight-medium);
    line-height: 1.4;
    margin: 0;
    letter-spacing: -0.02em;
    overflow-wrap: break-word;
  }

  &__description {
    font-family: var(--font-family-poppins);
    font-size: var(--typography-size-m);
    font-weight: var(--font-weight-book);
    line-height: 1.6;
    margin: 0;
    max-width: 400px;
    overflow-wrap: break-word;
    opacity: 0.95;
  }

  &__ctas {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 48px;
  }

  &__cta {
    flex-shrink: 0;
    min-width: 160px;
  }

  &__image {
    flex: 1;
    position: relative;
    overflow: hidden;
    height: 100%;
    background-color: var(--color-light-grey);

    &-element {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      transition: transform var(--timing-slow) var(--easing-default);

      .horizontal-card:hover & {
        transform: scale(1.02);
      }
    }
  }

  // Mobile responsive design - stack vertically
  @include mq($until: tablet) {
    flex-direction: column;
    height: auto;
    min-height: auto;
    gap: 0;

    &__content {
      padding: var(--space-m) var(--space-n);
      text-align: left;
      order: 1;
    }

    &__title {
      font-size: var(--typography-size-l-mobile, 24px);
    }

    &__description {
      font-size: var(--typography-size-m-mobile, 18px);
      max-width: none;
    }

    &__ctas {
      justify-content: flex-start;
      margin-top: var(--space-l);
    }

    &__image {
      height: 160px;
      order: 2;
      flex: none;

      &-element {
        object-fit: cover;
        object-position: center;
        width: 100%;
        height: 100%;
      }
    }
  }

  // Small mobile adjustments
  @include mq($until: mobile) {
    &__content {
      padding: var(--space-m) var(--space-n);
    }

    &__ctas {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-xs);
    }

    &__cta {
      width: 100%;
      max-width: 280px;
      min-width: auto;
    }
  }
}
</style>
